import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { MouseEvent, useEffect, useState } from "react";

import { RootState } from "store";
import classNames from "classnames";
import {
  Button,
  NotifierNotification,
  TrashIcon,
  UnsavedChangesPrompt,
  Upload,
} from "@mwi/ui";
import ImageSlider from "components/sliders/image_slider";
import { useUpdatePracticeThemeMutation, useUploadFileMutation } from "api";
import { useNotifier } from "react-headless-notifier";
import { setTheme } from "slices/practice";
import useTitle from "hooks/useTitle";

type SelectedImage = {
  id: number | string | null;
  url: string | null;
};

type Input = {
  custom_login_image: {
    id: number | null;
    url: string | null;
  };
  default_login_image: {
    id: string | null;
    url: string | null;
  };
};

const LoginImagePage = (): JSX.Element => {
  useTitle("Login Image");

  const dispatch = useDispatch();
  const { notify } = useNotifier();

  const { practice, theme } = useSelector((state: RootState) => ({
    practice: state.practice,
    theme: state.practice.theme,
  }));

  const [selectedLoginImage, setSelectedLoginImage] = useState<SelectedImage>();

  const {
    control,
    watch,
    getValues,
    setValue,
    reset,
    formState: { isDirty },
  } = useForm<Input>({
    defaultValues: {
      default_login_image:
        theme.defaultLoginImages?.find(
          (img) => img.id === theme.defaultLoginImage,
        ) ?? theme.defaultLoginImages[0],
      custom_login_image: {
        id: theme?.customLoginImageFileId ?? null,
        url: theme?.customLoginImage ?? null,
      },
    },
  });

  const [uploadFile] = useUploadFileMutation();

  const [
    updateTheme,
    {
      data: updatedTheme,
      isLoading: updatingTheme,
      isSuccess: didUpdateTheme,
      isError: errorUpdatingTheme,
    },
  ] = useUpdatePracticeThemeMutation();

  const handleSave = () => {
    if (getValues("default_login_image.id") === selectedLoginImage?.id) {
      setValue("custom_login_image", { id: null, url: null });
    }

    if (practice?.id) {
      updateTheme({
        practiceId: practice.id,
        body: {
          custom_login_image:
            getValues("custom_login_image.id") === selectedLoginImage?.id
              ? selectedLoginImage?.id
              : null,
          default_login_image:
            getValues("default_login_image.id") === selectedLoginImage?.id
              ? selectedLoginImage?.id
              : null,
        },
      });
    }
  };

  const handleCustomSelected = () => {
    setSelectedLoginImage({ ...watch("custom_login_image") });
    setValue("default_login_image", { id: null, url: null });
  };

  useEffect(() => {
    if (updatedTheme) {
      dispatch(setTheme(updatedTheme));

      // Reset fields being marked as dirty
      reset({
        default_login_image:
          theme?.defaultLoginImages?.find(
            (img) => img.id === updatedTheme?.default_login_image,
          ) ?? theme.defaultLoginImages[0],
        custom_login_image: {
          id: getValues("custom_login_image.id") ?? null,
          url: getValues("custom_login_image.url") ?? null,
        },
      });
    }
  }, [updatedTheme]);

  useEffect(() => {
    if (getValues("default_login_image.url")) {
      setSelectedLoginImage({ ...watch("default_login_image") });
    }
  }, [watch("default_login_image")]);

  useEffect(() => {
    if (getValues("custom_login_image.url")) {
      handleCustomSelected();
    }
  }, [watch("custom_login_image")]);

  useEffect(() => {
    if (didUpdateTheme) {
      notify(
        <NotifierNotification
          title="Theme Updated"
          message="Practice login image has been updated!"
        />,
      );
    }
  }, [didUpdateTheme]);

  useEffect(() => {
    if (errorUpdatingTheme) {
      notify(
        <NotifierNotification
          type="error"
          title="Error Updating Theme"
          message="We're unable to update the practice login image at the moment, please try again later."
        />,
      );
    }
  }, [errorUpdatingTheme]);

  return (
    <UnsavedChangesPrompt isDirty={isDirty}>
      <div className="grid tablet:grid-cols-8 desktop:grid-cols-10 !pb-16 gap-x-6">
        <div className="flex flex-col gap-y-[2px] col-span-full mb-14 text-lg">
          <p className="font-semibold">
            Choose a photo that represents your brand
          </p>
          <p>You can pick a photo from our gallery or upload your own. </p>
          <p>This will be visible on the login page for desktop users.</p>
        </div>

        <div className="mb-32 col-span-full">
          <div className="flex p-8 bg-white shadow-default gap-x-16">
            {/* Image preview */}
            <div className="flex-shrink-0 w-1/3 max-h-[566px] overflow-hidden">
              {selectedLoginImage?.url && (
                <img
                  className="object-cover w-full h-full"
                  src={selectedLoginImage.url}
                  alt=""
                />
              )}
            </div>

            <div className="flex flex-col justify-between w-[calc(66%-4rem)] gap-y-[72px]">
              <div>
                <div className="mb-5">
                  <h3>Choose from gallery</h3>
                </div>

                <div className="relative px-11 max-w-[539px] w-full overflow-hidden">
                  <ImageSlider
                    name="default_login_image"
                    control={control}
                    slidesPerView={4}
                    slides={theme?.defaultLoginImages}
                  />
                </div>
              </div>

              <div>
                <div className="mb-6">
                  <h3>Upload your own</h3>
                </div>

                <Upload
                  droppable
                  name="custom_login_image"
                  control={control}
                  uploadFile={uploadFile}
                  type="custom_login_images"
                  containerClassName="h-[250px]"
                  defaultUrl={theme?.customLoginImage}
                  deleteFile={() => setValue("custom_login_image.id", null)}
                  text="Drag & drop picture or click to browse"
                  description="File must be JPG or PNG and photo less than 2 MB in size"
                  renderPreview={({ previewUrl, removeImage }) => {
                    const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();

                      if (!getValues("default_login_image.url")) {
                        setValue(
                          "default_login_image",
                          theme.defaultLoginImages[0],
                        );
                      }

                      if (removeImage) {
                        removeImage();
                      }
                    };

                    return (
                      <div
                        onClick={handleCustomSelected}
                        className={classNames(
                          "relative h-full p-[6px] border-2 border-white max-w-[190px] cursor-pointer",
                          {
                            "!border-primary":
                              selectedLoginImage?.url === previewUrl,
                            "hover:border-grey-light":
                              selectedLoginImage?.url !== previewUrl,
                          },
                        )}
                      >
                        <button
                          onClick={(e: MouseEvent<HTMLButtonElement>) =>
                            handleRemove(e)
                          }
                          className="absolute grid w-8 h-8 rounded-full right-4 top-4 place-content-center bg-error-lightest"
                        >
                          <TrashIcon className="w-6 h-6 pointer-events-none text-error" />
                          <span className="sr-only">Discard image</span>
                        </button>
                        {previewUrl && (
                          <img
                            className="object-cover w-full h-full"
                            src={previewUrl}
                            alt=""
                          />
                        )}
                      </div>
                    );
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-full max-w-[185px]">
          <Button
            block
            type="button"
            onClick={handleSave}
            loading={updatingTheme}
            disabled={errorUpdatingTheme}
          >
            Save changes
          </Button>
        </div>
      </div>
    </UnsavedChangesPrompt>
  );
};

export default LoginImagePage;
